/**
 * Shared styles for the zkML Perplexity UI
 * 
 * This file contains reusable style classes to maintain consistency
 * across the application and reduce duplication.
 * Colors now come from colors.css as class names.
 */

// Export empty objects to satisfy imports now that colors.ts is gone
// The actual styling is now provided by colors.css utility classes
export const colors = {};
export const colorValues = {};

// Typography
export const typography = {
  // Font sizes
  size: {
    tiny: 'text-[9px]',
    xs: 'text-[11px]',
    sm: 'text-xs',
    base: 'text-sm',
    lg: 'text-base',
    xl: 'text-lg',
    '2xl': 'text-xl',
    '3xl': 'text-2xl',
  },
  
  // Font weights
  weight: {
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
  },
  
  // Common text styles
  styles: {
    heading: 'font-semibold text-xl text-primary',
    subheading: 'font-medium text-sm text-secondary',
    label: 'font-medium text-xs uppercase text-tertiary tracking-wider',
    body: 'text-sm text-secondary',
    small: 'text-xs text-tertiary',
    tiny: 'text-[11px] text-tertiary',
  }
};

// Buttons
export const buttons = {
  // Primary button
  primary: 'bg-accent-main text-primary hover:bg-accent transition-colors',
  
  // Secondary button
  secondary: 'bg-component text-secondary hover:bg-component-light transition-colors',
  
  // Tertiary button
  tertiary: 'bg-hover text-secondary hover:bg-active transition-colors',
  
  // Disabled state
  disabled: 'bg-hover text-disabled cursor-not-allowed',
  
  // Common shapes
  shapes: {
    pill: 'rounded-full',
    rounded: 'rounded-md',
    roundedLg: 'rounded-lg',
  },
  
  // Common sizes
  sizes: {
    icon: 'p-1',
    small: 'px-2 py-1 text-xs',
    medium: 'px-3 py-1.5 text-sm',
    large: 'px-4 py-2',
  },
  
  // For sidebar or nav items
  navItem: 'flex items-center gap-2.5 py-2 px-3 rounded-md hover:bg-component-light transition-colors',
};

// Form Elements
export const forms = {
  // Input styles
  input: 'rounded-md border border-contrast shadow-sm focus:outline-none focus:ring-1 focus:ring-accent bg-contrast text-gray-700',
  inputSizes: {
    sm: 'h-8 px-3 py-1 text-xs',
    md: 'h-10 px-3 py-2 text-sm',
    lg: 'h-12 px-4 py-2.5',
  },
  inputShapes: {
    default: 'rounded-md',
    pill: 'rounded-full',
  },
  
  // Dark theme input
  darkInput: 'rounded-md border border-main bg-component text-primary focus:border-accent placeholder-gray-400',
  
  // Textarea
  textarea: 'w-full py-2.5 px-3 outline-none resize-none text-gray-700 rounded-md border border-contrast focus:outline-none focus:ring-1 focus:ring-accent',
  
  // Dark theme textarea
  darkTextarea: 'w-full py-2.5 px-3 outline-none resize-none text-primary rounded-md border border-main bg-component focus:border-accent',
};

// Layout & Spacing
export const layout = {
  // Flexbox utilities
  flex: {
    row: 'flex flex-row',
    col: 'flex flex-col',
    center: 'flex items-center justify-center',
    between: 'flex justify-between items-center',
    start: 'flex justify-start items-center',
    end: 'flex justify-end items-center',
  },
  
  // Common containers
  container: {
    card: 'bg-component border border-main rounded-md p-4 shadow-sm',
    section: 'max-w-3xl mx-auto px-4 sm:px-6 lg:px-8',
    sidebar: 'h-screen bg-page text-primary-inverse flex flex-col',
  },
  
  // Z-index layers
  zIndex: {
    base: 'z-0',
    above: 'z-10',
    modal: 'z-40',
    modalContent: 'z-50',
  },
};

// Animations & Transitions
export const animations = {
  transition: {
    default: 'transition-all duration-200 ease-in-out',
    slow: 'transition-all duration-300 ease-in-out',
    fast: 'transition-all duration-100 ease-in-out',
    color: 'transition-colors duration-150',
  },
  transform: {
    hover: 'hover:scale-105',
    active: 'active:scale-95',
    rotate: 'hover:rotate-90',
    slideIn: 'translate-x-0',
    slideOut: '-translate-x-full',
  },
  
  // For staggered animations
  fadeIn: {
    base: 'transition-opacity duration-200 ease-out',
    visible: 'opacity-100',
    hidden: 'opacity-0',
    delay1: 'delay-75',
    delay2: 'delay-150',
    delay3: 'delay-200',
  },
};

// Common utility combinations
export const utils = {
  // Message bubble style for the chat
  messageBubble: {
    user: 'bg-accent-main text-primary rounded-lg p-3.5 max-w-[85%]',
    bot: 'bg-component border border-main rounded-lg p-3.5 shadow-sm max-w-[85%] text-primary',
  },
  
  // Content sections
  contentSection: {
    title: 'font-medium text-xs text-secondary mb-2',
    container: 'mb-6',
  },
  
  // Interactive elements
  interactive: {
    hover: 'hover:shadow-md transition-shadow cursor-pointer',
    active: 'active:scale-[0.98] transition-transform',
    focusRing: 'focus:outline-none focus:ring-2 focus:ring-accent focus:ring-opacity-50',
  },
  
  // Common shadows
  shadow: {
    sm: 'shadow-sm',
    md: 'shadow',
    lg: 'shadow-md',
    xl: 'shadow-lg',
  },
};

// Helper function to combine multiple classes
export const cx = (...classes: (string | undefined | null | false)[]) => {
  return classes.filter(Boolean).join(' ');
};
